/**
 * ObjetsTrouvesManager — bloc de gestion des objets trouvés (mairie / GRC).
 *
 * Encapsule tout le CRUD objets trouvés pour l'écran « Services & vie
 * quotidienne » : liste (filtrée par statut), déclaration, upload photo R2,
 * changement de statut (à réclamer → rendu / archivé), suppression.
 *
 * Conforme au guide : zéro window.confirm (useConfirmDialog + {dialogElement}
 * monté ici), photo via FormData (api.grc.objets.uploadPhoto).
 *
 * Expose `onEtatChange` pour que le parent rafraîchisse le badge d'état de la
 * carte (actif / à configurer).
 */

import { useEffect, useState, useCallback, useRef } from 'react';
import { Card, Button, Badge, Alert, Field, Input } from '@/components/ui';
import { Dialog } from '@/components/ui/Dialog';
import { useConfirmDialog } from '@/lib/use-confirm-dialog';
import { api, ApiException } from '@/lib/api';
import type { ObjetTrouve, ObjetStatut } from '@/lib/api';
import {
  Plus, Pencil, Trash2, ImagePlus, PackageCheck, Archive, RotateCcw, AlertCircle, MapPin,
} from 'lucide-react';

const STATUT_LABEL: Record<ObjetStatut, string> = {
  a_reclamer: 'À réclamer',
  rendu: 'Rendu',
  archive: 'Archivé',
};

/** Date pure yyyy-mm-dd → « 28 juin 2026 » (UTC, sans décalage). */
function formatDatePure(dateStr: string): string {
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(Date.UTC(y!, m! - 1, d!)).toLocaleDateString('fr-FR', {
    day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC',
  });
}

/** Date du jour en zone Europe/Paris (défaut du formulaire). */
function todayParis(): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Paris', year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(new Date());
}

export function ObjetsTrouvesManager({ onEtatChange }: { onEtatChange?: () => void }) {
  const { confirm, dialogElement } = useConfirmDialog();

  const [objets, setObjets] = useState<ObjetTrouve[]>([]);
  const [filtre, setFiltre] = useState<ObjetStatut | 'tous'>('a_reclamer');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [edit, setEdit] = useState<ObjetTrouve | 'new' | null>(null);

  const reload = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await api.grc.objets.list(filtre === 'tous' ? undefined : filtre);
      setObjets(res.objets);
    } catch (e) {
      setError(e instanceof ApiException ? e.message : 'Chargement impossible');
    } finally {
      setLoading(false);
    }
  }, [filtre]);

  useEffect(() => { void reload(); }, [reload]);

  async function refreshAll() {
    await reload();
    onEtatChange?.();
  }

  async function setStatut(o: ObjetTrouve, statut: ObjetStatut, renduA?: string) {
    try {
      await api.grc.objets.changerStatut(o.id, { statut, renduA });
      await refreshAll();
    } catch (e) {
      setError(e instanceof ApiException ? e.message : 'Action impossible');
    }
  }

  async function supprimer(o: ObjetTrouve) {
    const ok = await confirm({
      title: 'Supprimer cet objet ?',
      description: `« ${o.libelle} » sera définitivement supprimé du registre.`,
      confirmLabel: 'Supprimer', danger: true,
    });
    if (!ok) return;
    try { await api.grc.objets.remove(o.id); await refreshAll(); }
    catch (e) { setError(e instanceof ApiException ? e.message : 'Suppression impossible'); }
  }

  const filtres: { key: ObjetStatut | 'tous'; label: string }[] = [
    { key: 'a_reclamer', label: 'À réclamer' },
    { key: 'rendu', label: 'Rendus' },
    { key: 'archive', label: 'Archivés' },
    { key: 'tous', label: 'Tous' },
  ];

  return (
    <div>
      {error && <Alert variant="error" icon={AlertCircle} className="mb-3">{error}</Alert>}

      <div className="mb-3 flex items-center justify-between gap-2">
        <div className="flex flex-wrap gap-1.5">
          {filtres.map((f) => (
            <button
              key={f.key}
              type="button"
              onClick={() => setFiltre(f.key)}
              className={
                filtre === f.key
                  ? 'rounded-r-pill border border-grc/30 bg-grc-tint/80 px-3 py-1 text-xs font-medium text-grc-fg'
                  : 'rounded-r-pill border border-transparent px-3 py-1 text-xs font-medium text-ink-muted hover:border-border hover:text-ink'
              }
            >
              {f.label}
            </button>
          ))}
        </div>
        <Button variant="ghost" size="sm" onClick={() => setEdit('new')}>
          <Plus className="h-4 w-4" /> Déclarer
        </Button>
      </div>

      {loading ? (
        <p className="text-sm text-ink-muted">Chargement…</p>
      ) : objets.length === 0 ? (
        <p className="text-sm text-ink-muted">
          Aucun objet {filtre !== 'tous' ? `« ${STATUT_LABEL[filtre as ObjetStatut].toLowerCase()} »` : ''}.
          Déclarez un objet trouvé à l'accueil pour qu'il apparaisse aux administrés.
        </p>
      ) : (
        <ul className="space-y-2">
          {objets.map((o) => (
            <li key={o.id} className="flex items-start gap-3 rounded-r-lg border border-border bg-surface/50 p-3">
              {o.photoUrl && (
                <img src={o.photoUrl} alt={o.libelle} loading="lazy"
                  className="h-14 w-14 flex-shrink-0 rounded-r-md border border-border object-cover" />
              )}
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-ink">{o.libelle}</span>
                  <Badge
                    variant={o.statut === 'a_reclamer' ? 'warning' : o.statut === 'rendu' ? 'success' : 'neutral'}
                    size="sm"
                  >
                    {STATUT_LABEL[o.statut]}
                  </Badge>
                </div>
                {o.description && <p className="mt-0.5 text-sm text-ink-muted">{o.description}</p>}
                <div className="mt-1 flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-ink-dim">
                  <span>Trouvé le {formatDatePure(o.dateTrouve)}</span>
                  {o.lieu && <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{o.lieu}</span>}
                  {o.statut === 'rendu' && o.renduA && <span>Rendu à {o.renduA}</span>}
                </div>
                {/* Actions */}
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {o.statut === 'a_reclamer' && (
                    <>
                      <Button variant="ghost" size="sm" onClick={() => setEdit(o)} title="Modifier / photo">
                        <Pencil className="h-3.5 w-3.5" /> Modifier
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => void setStatut(o, 'rendu')} title="Marquer rendu">
                        <PackageCheck className="h-3.5 w-3.5" /> Rendu
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => void setStatut(o, 'archive')} title="Archiver">
                        <Archive className="h-3.5 w-3.5" /> Archiver
                      </Button>
                    </>
                  )}
                  {(o.statut === 'rendu' || o.statut === 'archive') && (
                    <Button variant="ghost" size="sm" onClick={() => void setStatut(o, 'a_reclamer')} title="Remettre à réclamer">
                      <RotateCcw className="h-3.5 w-3.5" /> Remettre en liste
                    </Button>
                  )}
                  <Button variant="ghost" size="sm" onClick={() => void supprimer(o)} title="Supprimer">
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      {edit && (
        <ObjetDialog
          initial={edit === 'new' ? null : edit}
          onClose={() => setEdit(null)}
          onSaved={async () => { setEdit(null); await refreshAll(); }}
        />
      )}
      {dialogElement}
    </div>
  );
}

// ============================================================================
// Modale — déclaration / modification (+ upload photo)
// ============================================================================

function ObjetDialog({
  initial, onClose, onSaved,
}: {
  initial: ObjetTrouve | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [libelle, setLibelle] = useState(initial?.libelle ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [lieu, setLieu] = useState(initial?.lieu ?? '');
  const [dateTrouve, setDateTrouve] = useState(initial?.dateTrouve ?? todayParis());
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [photoUrl, setPhotoUrl] = useState<string | null>(initial?.photoUrl ?? null);
  const [objetId, setObjetId] = useState<string | null>(initial?.id ?? null);
  const fileRef = useRef<HTMLInputElement>(null);

  async function save() {
    setBusy(true); setErr(null);
    try {
      const body = {
        libelle: libelle.trim(),
        description: description.trim() || null,
        lieu: lieu.trim() || null,
        dateTrouve,
      };
      let id = objetId;
      if (initial) {
        await api.grc.objets.update(initial.id, body);
      } else {
        const created = await api.grc.objets.create(body);
        id = created.id;
        setObjetId(id);
      }
      // Si une photo est en attente (nouvelle déclaration), on l'envoie après création.
      const file = fileRef.current?.files?.[0];
      if (file && id) {
        const withPhoto = await api.grc.objets.uploadPhoto(id, file);
        setPhotoUrl(withPhoto.photoUrl);
      }
      onSaved();
    } catch (e) {
      setErr(e instanceof ApiException ? e.message : 'Enregistrement impossible');
      setBusy(false);
    }
  }

  // Upload immédiat de la photo si l'objet existe déjà (modification).
  async function onPhotoPick() {
    const file = fileRef.current?.files?.[0];
    if (!file) return;
    if (!objetId) return; // sera envoyée au save (nouvelle déclaration)
    setBusy(true); setErr(null);
    try {
      const withPhoto = await api.grc.objets.uploadPhoto(objetId, file);
      setPhotoUrl(withPhoto.photoUrl);
    } catch (e) {
      setErr(e instanceof ApiException ? e.message : 'Envoi de la photo impossible');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open onClose={onClose} title={initial ? 'Modifier l\'objet' : 'Déclarer un objet trouvé'}>
      <div className="space-y-3">
        {err && <Alert variant="error" icon={AlertCircle}>{err}</Alert>}
        <Field label="Objet" htmlFor="obj-libelle" required>
          <Input id="obj-libelle" value={libelle} onChange={(e) => setLibelle(e.target.value)} placeholder="Trousseau de clés, portefeuille…" />
        </Field>
        <Field label="Description (optionnel)" htmlFor="obj-desc">
          <textarea
            id="obj-desc"
            className="w-full rounded-r-md border border-border bg-bg px-3 py-2 text-sm text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-grc"
            rows={2}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Couleur, marque, contenu visible…"
          />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Lieu (optionnel)" htmlFor="obj-lieu">
            <Input id="obj-lieu" value={lieu} onChange={(e) => setLieu(e.target.value)} placeholder="Place de l'Église" />
          </Field>
          <Field label="Date de découverte" htmlFor="obj-date" required>
            <Input id="obj-date" type="date" value={dateTrouve} onChange={(e) => setDateTrouve(e.target.value)} />
          </Field>
        </div>

        {/* Photo */}
        <div className="flex items-center gap-3">
          {photoUrl
            ? <img src={photoUrl} alt="Aperçu" className="h-16 w-16 rounded-r-md border border-border object-cover" />
            : <div className="flex h-16 w-16 items-center justify-center rounded-r-md border border-dashed border-border text-ink-dim"><ImagePlus className="h-5 w-5" /></div>}
          <div>
            <input ref={fileRef} type="file" accept="image/*" onChange={onPhotoPick} className="hidden" id="obj-photo" />
            <Button variant="ghost" size="sm" onClick={() => fileRef.current?.click()} disabled={busy}>
              <ImagePlus className="h-4 w-4" /> {photoUrl ? 'Changer la photo' : 'Ajouter une photo'}
            </Button>
            {!objetId && <p className="mt-1 text-xs text-ink-dim">La photo sera envoyée à l'enregistrement.</p>}
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onClose} disabled={busy}>Annuler</Button>
          <Button variant="primary" onClick={save} disabled={busy || !libelle.trim()}>
            {busy ? 'Enregistrement…' : 'Enregistrer'}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
